import 'maplibre-gl/dist/maplibre-gl.css';
import { useEffect, useRef, useState } from 'react';
import maplibregl, { Map as MlMap, type StyleSpecification } from 'maplibre-gl';
import { api } from '../../api/client';
import type { MbtilesEntry } from '../../api/types';
import { ExampleControls } from './Controls';
import { InfoPanel } from '../../components/InfoPanel';
import {
  buildTileJsonUrl,
  classifyFormat,
  defaultBaseUrl,
  fetchTileJson,
  resolveTileUrl,
  withTokenQuery,
  type AccessMode,
  type TileJsonResponse,
} from './shared';

// OSM 배경. tile usage policy 상 프로덕션 사용은 제한 — 본 페이지는 개발 데모.
const OSM_SOURCE: StyleSpecification['sources'][string] = {
  type: 'raster',
  tiles: [
    'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
    'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
    'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
  ],
  tileSize: 256,
  attribution: '© OpenStreetMap contributors',
};
const OSM_LAYER: StyleSpecification['layers'][number] = {
  id: 'osm-basemap',
  type: 'raster',
  source: 'osm',
};

function buildStyle(tj: TileJsonResponse, tilesUrl: string): StyleSpecification {
  const kind = classifyFormat(tj.format);
  if (kind === 'raster') {
    return {
      version: 8,
      sources: {
        osm: OSM_SOURCE,
        'mbtiler-src': {
          type: 'raster',
          tiles: [tilesUrl],
          tileSize: 256,
          minzoom: tj.minzoom,
          maxzoom: tj.maxzoom,
          bounds: tj.bounds,
        },
      },
      layers: [
        OSM_LAYER,
        { id: 'mbtiler-raster', type: 'raster', source: 'mbtiler-src' },
      ],
    };
  }

  // vector: vector_layers를 자동으로 선/면 디버그 스타일로 그린다.
  // (mbtiles별 의미를 모르므로 단색 outline.)
  const vectorLayers = tj.vector_layers ?? [];
  const layers: StyleSpecification['layers'] = [OSM_LAYER];
  for (const vl of vectorLayers) {
    layers.push({
      id: `mbtiler-fill-${vl.id}`,
      type: 'fill',
      source: 'mbtiler-src',
      'source-layer': vl.id,
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: {
        'fill-color': '#2563eb',
        'fill-opacity': 0.1,
        'fill-outline-color': '#2563eb',
      },
    });
    layers.push({
      id: `mbtiler-line-${vl.id}`,
      type: 'line',
      source: 'mbtiler-src',
      'source-layer': vl.id,
      filter: ['==', ['geometry-type'], 'LineString'],
      paint: { 'line-color': '#2563eb', 'line-width': 1 },
    });
    layers.push({
      id: `mbtiler-circle-${vl.id}`,
      type: 'circle',
      source: 'mbtiler-src',
      'source-layer': vl.id,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-color': '#2563eb', 'circle-radius': 2 },
    });
  }
  return {
    version: 8,
    sources: {
      osm: OSM_SOURCE,
      'mbtiler-src': {
        type: 'vector',
        tiles: [tilesUrl],
        minzoom: tj.minzoom,
        maxzoom: tj.maxzoom,
        bounds: tj.bounds,
      },
    },
    layers,
  };
}

export function MapLibreExamplePage() {
  const [baseUrl, setBaseUrl] = useState<string>(() => defaultBaseUrl());
  const [mbtiles, setMbtiles] = useState<MbtilesEntry[] | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');
  const [mode, setMode] = useState<AccessMode>('public');
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mapRef = useRef<MlMap | null>(null);
  const mapDivRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    api
      .listMbtiles()
      .then((m) => {
        setMbtiles(m);
        if (m.length > 0) setSelectedId(m[0].id);
      })
      .catch((err: Error) => setError(err.message));
  }, []);

  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;
    mapRef.current = new maplibregl.Map({
      container: mapDivRef.current,
      style: { version: 8, sources: { osm: OSM_SOURCE }, layers: [OSM_LAYER] },
      center: [127.5, 36.5],
      zoom: 5,
    });
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !selectedId) return;
    const map = mapRef.current;

    let cancelled = false;
    setError(null);

    const tileJsonUrl = buildTileJsonUrl(baseUrl, selectedId, mode, token);

    fetchTileJson(tileJsonUrl)
      .then((tj) => {
        if (cancelled || !mapRef.current) return;
        const rawTilesUrl = tj.tiles[0];
        if (!rawTilesUrl) {
          setError('TileJSON에 tiles[] 가 비었습니다.');
          return;
        }
        const tilesUrl = resolveTileUrl(baseUrl, rawTilesUrl);
        const finalUrl = mode === 'token' ? withTokenQuery(tilesUrl, token) : tilesUrl;
        map.setStyle(buildStyle(tj, finalUrl));

        if (tj.center) {
          // center z 가 maxzoom 끝으로 박혀 있으면 폴리곤이 화면을 가득 채워 빈 것처럼
          // 보인다 — maxzoom 과 OSM 가용 범위(≤19) 한도로 클램프.
          const cz = tj.center[2];
          const safeZoom =
            cz !== undefined ? Math.min(cz, tj.maxzoom ?? 19, 18) : map.getZoom();
          map.jumpTo({
            center: [tj.center[0], tj.center[1]],
            zoom: safeZoom,
          });
        } else if (tj.bounds) {
          const [w, s, e, n] = tj.bounds;
          // bounds 가 좁을 때 fitBounds 가 source 한계(maxzoom) 너머까지 줌인 → 404 폭주 방지.
          map.fitBounds([
            [w, s],
            [e, n],
          ], { padding: 20, animate: false, maxZoom: tj.maxzoom });
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [baseUrl, selectedId, mode, token]);

  return (
    <>
      <h2>MapLibre GL 예제</h2>
      <InfoPanel
        what={
          <p style={{ margin: 0 }}>
            MapLibre 의 style spec 으로 mbtiler 의 raster 또는 vector source 를
            등록해 렌더링합니다. TileJSON 의 <code>format</code> 으로 자동 분기하고,
            vector 면 <code>vector_layers[]</code> 마다 단색 디버그 스타일을 자동
            생성합니다. 배경엔 OSM raster 를 깝니다.
          </p>
        }
        why={
          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
            <li>
              vector tile 이 본업인 라이브러리에서 mbtiler 통합 패턴 — style JSON 만
              들고 있으면 다른 vector tile 인프라와도 호환.
            </li>
            <li>
              public / read-token 모드 토글로 권한 게이트가 의도대로 동작하는지
              직접 검증.
            </li>
            <li>
              vector 의 자동 스타일은 데이터 점검용 — 실 사용 시 mbtiles 의미에 맞는
              자체 스타일을 작성하세요.
            </li>
          </ul>
        }
      />
      {error && (
        <div className="banner error" data-testid="example-error">
          {error}
        </div>
      )}
      <ExampleControls
        baseUrl={baseUrl}
        onChangeBaseUrl={setBaseUrl}
        mbtiles={mbtiles}
        selectedId={selectedId}
        onSelectId={setSelectedId}
        mode={mode}
        onChangeMode={setMode}
        token={token}
        onChangeToken={setToken}
      />
      <div
        ref={mapDivRef}
        data-testid="example-map"
        style={{ width: '100%', height: 520, borderRadius: 6, overflow: 'hidden' }}
      />
    </>
  );
}
